import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  ensureShareLink,
  getCandidates,
  getJob,
  submitAccept,
  submitDurationRerender,
  submitRefine,
} from '../api/rttsClient';
import type {
  CandidatesSuccess,
  Entitlement,
  HighlightJob,
  ReelDurationTier,
  RefineClip,
  RefineMusicOptions,
} from '../api/types';
import {
  AcquireError,
  isMonthlyCapReachedError,
  isUpgradeRequiredError,
} from '../api/types';
import {
  mapDurationRerenderError,
  type MappedDurationRerender,
} from '../lib/mapDurationRerenderError';
import {
  POLL_INTERVAL_MS,
  STUDIO_COPY,
  STUDIO_PRICING,
  formatAllotmentCopy,
  formatCreditBalanceCopy,
  formatAcceptCostFreeCopy,
  formatRecutCostFreeCopy,
  isStudioBillingEnabled,
  isStudioMusicEnabled,
  isStudioReelPacksEnabled,
} from '../config';
import {
  defaultGameGainDb,
  defaultMusicGainDb,
} from '../music/loadTracks';
import {
  DEFAULT_MUSIC_FADE_IN_S,
  DEFAULT_MUSIC_FADE_OUT_S,
  DEFAULT_MUSIC_LOOP,
} from '../music/types';
import { formatDuration } from '../utils/format';
import { isUnlimitedEntitlement } from '../utils/vodEligibility';
import ReelDownloadButton from './ReelDownloadButton';
import {
  isReelNameValid,
  normalizeReelName,
} from '../utils/reelName';
import ReelNameField from './ReelNameField';
import DurationTierPicker from './DurationTierPicker';
import RefineReviewPanel from './RefineReviewPanel';
import AddOnsPanel from './AddOnsPanel';

const REBUILD_POLL_MAX_MS = 15 * 60_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

type ReelResultPanelProps = {
  job: HighlightJob;
  entitlement: Entitlement | null;
  alreadyExists?: boolean;
  /** When set with alreadyExists + recut_enabled, show metered re-cut CTA. */
  onRecut?: (reelName: string, options?: { force?: boolean }) => void | Promise<void>;
  recutBusy?: boolean;
  recutError?: string | null;
  /** After a refine/accept enqueue, parent should poll until delivered again. */
  onRefineStarted?: (job: HighlightJob) => void;
  onAcceptStarted?: (job: HighlightJob) => void;
  onEntitlementRefresh?: () => void | Promise<void>;
};

function remainingAccepted(entitlement: Entitlement | null): number {
  if (!entitlement || isUnlimitedEntitlement(entitlement)) return 999;
  if (entitlement.signoff_billing_enabled) {
    if (entitlement.tier === 'paid') {
      return Math.max(
        0,
        (entitlement.accepted_max ?? entitlement.period_cap ?? 0) -
          (entitlement.accepted_used ?? 0)
      );
    }
    return Math.max(
      0,
      (entitlement.accepted_max ?? entitlement.free_cap ?? 0) -
        (entitlement.accepted_used ?? 0)
    );
  }
  if (entitlement.tier === 'paid') {
    return entitlement.period_reels_remaining ?? 0;
  }
  return entitlement.reels_remaining ?? 0;
}

function acceptCostCopy(entitlement: Entitlement | null): string {
  if (!entitlement || isUnlimitedEntitlement(entitlement)) {
    return STUDIO_COPY.acceptCostUnlimited;
  }
  const remaining = remainingAccepted(entitlement);
  if (entitlement.tier === 'paid') {
    return STUDIO_COPY.acceptCostTemplate.replace(
      '{remaining}',
      String(remaining)
    );
  }
  return formatAcceptCostFreeCopy(remaining);
}

function recutCostCopy(entitlement: Entitlement | null): string {
  if (entitlement?.recut_reuse_source) {
    return STUDIO_COPY.recutReuseCost;
  }
  if (!entitlement || isUnlimitedEntitlement(entitlement)) {
    return STUDIO_COPY.recutCostUnlimited;
  }
  if (entitlement.signoff_billing_enabled) {
    const ceiling = entitlement.generation_ceiling ?? 0;
    const used = entitlement.generations_used ?? 0;
    const remaining = Math.max(0, ceiling - used);
    if (entitlement.tier === 'paid') {
      return STUDIO_COPY.recutGenCostTemplate
        .replace('{remaining}', String(remaining))
        .replace('{ceiling}', String(ceiling));
    }
    return STUDIO_COPY.recutGenCostFreeTemplate
      .replace('{remaining}', String(remaining))
      .replace('{ceiling}', String(ceiling));
  }
  if (entitlement.tier === 'paid') {
    const remaining = entitlement.period_reels_remaining ?? 0;
    return STUDIO_COPY.recutCostTemplate.replace('{remaining}', String(remaining));
  }
  const remaining = entitlement.reels_remaining ?? 0;
  return formatRecutCostFreeCopy(remaining);
}

function isPreviewJob(
  job: HighlightJob,
  entitlement: Entitlement | null
): boolean {
  if (!entitlement?.signoff_billing_enabled) return false;
  if (job.accepted === true || Boolean(job.accepted_at)) return false;
  // Un-accepted jobs under signoff are watermarked previews.
  return true;
}

const ReelResultPanel: React.FC<ReelResultPanelProps> = ({
  job,
  entitlement,
  alreadyExists = false,
  onRecut,
  recutBusy = false,
  recutError = null,
  onRefineStarted,
  onAcceptStarted,
  onEntitlementRefresh,
}) => {
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recutName, setRecutName] = useState(() =>
    isReelNameValid(job.reel_name) ? normalizeReelName(job.reel_name) : ''
  );
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineLoading, setRefineLoading] = useState(false);
  const [refineBusy, setRefineBusy] = useState(false);
  const [refineError, setRefineError] = useState<string | null>(null);
  const [refinePayload, setRefinePayload] = useState<CandidatesSuccess | null>(
    null
  );
  const [refreshingPreview, setRefreshingPreview] = useState(false);
  const [rebuildingReel, setRebuildingReel] = useState(false);
  const [reelPreviewUrl, setReelPreviewUrl] = useState<string | null>(
    job.signed_url ?? null
  );
  const [reelPreviewError, setReelPreviewError] = useState<string | null>(null);
  const [workingJob, setWorkingJob] = useState(job);
  const [reelRebuilt, setReelRebuilt] = useState(false);
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [paywall, setPaywall] = useState<'upgrade' | 'cap' | null>(null);
  const [durationBusy, setDurationBusy] = useState(false);
  const [durationFailure, setDurationFailure] =
    useState<MappedDurationRerender | null>(null);
  const reelVideoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<HTMLDivElement | null>(null);
  const [recordDock, setRecordDock] = useState<HTMLDivElement | null>(null);
  const [voiceRecording, setVoiceRecording] = useState(false);

  const handleRecordingChange = useCallback((next: boolean) => {
    setVoiceRecording(next);
  }, []);

  useEffect(() => {
    if (!voiceRecording) return;
    playerRef.current?.scrollIntoView({
      block: 'start',
      inline: 'nearest',
      behavior: 'auto',
    });
  }, [voiceRecording]);

  useEffect(() => {
    setWorkingJob(job);
    setRecutName(
      isReelNameValid(job.reel_name) ? normalizeReelName(job.reel_name) : ''
    );
    if (!refineOpen) {
      setReelPreviewUrl(job.signed_url ?? null);
      setReelRebuilt(false);
    }
  }, [job, refineOpen]);

  const signedUrl = workingJob.signed_url ?? '';
  const previewVersion =
    workingJob.updated_at || workingJob.mix_status || workingJob.watermark_status || '';
  const playerSrc = signedUrl
    ? `${signedUrl}${signedUrl.includes('#') ? '&' : '#'}v=${encodeURIComponent(previewVersion)}`
    : '';
  const preview = isPreviewJob(workingJob, entitlement);
  const showFreeUpsell =
    isStudioBillingEnabled() &&
    entitlement?.tier === 'free' &&
    !isUnlimitedEntitlement(entitlement) &&
    (!entitlement.signoff_billing_enabled || paywall === 'upgrade');
  const isPaid = entitlement?.tier === 'paid';
  const durationSec =
    typeof workingJob.reel_duration_sec === 'number'
      ? workingJob.reel_duration_sec
      : null;
  const showRecut =
    Boolean(alreadyExists && onRecut && entitlement?.recut_enabled) ||
    Boolean(preview && onRecut && entitlement?.recut_enabled);
  const showRefine = Boolean(
    entitlement?.trim_enabled && workingJob.status === 'delivered' && workingJob.id
  );
  const curateEnabled = Boolean(entitlement?.curate_enabled && showRefine);
  const showCurationEntry = Boolean(
    entitlement?.customize_entry_enabled && showRefine
  );
  const musicEnabled = isStudioMusicEnabled();

  const initialMusic = ((): RefineMusicOptions | null => {
    if (!musicEnabled) return null;
    const mid = (workingJob.music_id || '').trim();
    return {
      music_id: mid || null,
      music_mode: workingJob.music_mode === 'music_only' ? 'music_only' : 'bed',
      game_gain_db:
        typeof workingJob.game_gain_db === 'number'
          ? workingJob.game_gain_db
          : defaultGameGainDb(),
      music_gain_db:
        typeof workingJob.music_gain_db === 'number'
          ? workingJob.music_gain_db
          : defaultMusicGainDb(),
      music_loop:
        typeof workingJob.music_loop === 'boolean'
          ? workingJob.music_loop
          : DEFAULT_MUSIC_LOOP,
      music_fade_in_s:
        typeof workingJob.music_fade_in_s === 'number'
          ? workingJob.music_fade_in_s
          : DEFAULT_MUSIC_FADE_IN_S,
      music_fade_out_s:
        typeof workingJob.music_fade_out_s === 'number'
          ? workingJob.music_fade_out_s
          : DEFAULT_MUSIC_FADE_OUT_S,
    };
  })();

  const handleCopy = async () => {
    if (preview) return;
    try {
      let url = workingJob.share_url ?? '';
      if (!url.startsWith('https://scoutlytics.gg/r/') && workingJob.id) {
        const minted = await ensureShareLink(workingJob.id);
        url = minted.share_url;
        setWorkingJob((prev) => ({ ...prev, share_url: url }));
      }
      if (!url.startsWith('https://scoutlytics.gg/r/')) return;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const openRefine = async () => {
    if (!workingJob.id || refineLoading) return;
    setRefineLoading(true);
    setRefineError(null);
    setReelPreviewError(null);
    try {
      const payload = await getCandidates(workingJob.id);
      setRefinePayload(payload);
      setReelPreviewUrl(workingJob.signed_url ?? null);
      setReelRebuilt(false);
      setRefineOpen(true);
    } catch {
      setRefineError(STUDIO_COPY.refineFailed);
    } finally {
      setRefineLoading(false);
    }
  };

  const refreshPreview = async () => {
    if (!workingJob.id || refreshingPreview) return;
    setRefreshingPreview(true);
    try {
      const payload = await getCandidates(workingJob.id);
      setRefinePayload(payload);
    } catch {
      setRefineError(STUDIO_COPY.refineFailed);
    } finally {
      setRefreshingPreview(false);
    }
  };

  const handleRefineSubmit = async (
    clips: RefineClip[],
    music?: RefineMusicOptions | null
  ): Promise<boolean> => {
    if (!workingJob.id || refineBusy || rebuildingReel) return false;
    setRefineBusy(true);
    setRefineError(null);
    setReelPreviewError(null);
    try {
      const { job: nextJob, docked_reel } = await submitRefine(
        workingJob.id,
        clips,
        musicEnabled ? music ?? null : undefined
      );
      if (docked_reel) {
        setRefineError(STUDIO_COPY.refineFailed);
        return false;
      }
      // Stay on Customize: poll until watermarked reel is ready for the player.
      setRebuildingReel(true);
      setReelPreviewUrl(null);
      setWorkingJob(nextJob);
      const jobId = nextJob.id;
      const deadline = Date.now() + REBUILD_POLL_MAX_MS;
      let delivered: HighlightJob | null = null;
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        const { job: polled } = await getJob(jobId);
        if (polled.status === 'failed') {
          setReelPreviewError(STUDIO_COPY.refineFailed);
          setWorkingJob(polled);
          break;
        }
        if (polled.status === 'delivered' && polled.signed_url) {
          delivered = polled;
          break;
        }
      }
      if (delivered) {
        setWorkingJob(delivered);
        setReelPreviewUrl(delivered.signed_url ?? null);
        setReelRebuilt(true);
        try {
          const payload = await getCandidates(jobId);
          setRefinePayload(payload);
        } catch {
          // Reel URL is enough; source remint can be retried via Refresh.
        }
        return true;
      }
      setReelPreviewError((prev) => prev || STUDIO_COPY.refineFailed);
      return false;
    } catch {
      setRefineError(STUDIO_COPY.refineFailed);
      setReelPreviewError(STUDIO_COPY.refineFailed);
      return false;
    } finally {
      setRebuildingReel(false);
      setRefineBusy(false);
    }
  };

  const closeRefine = () => {
    setRefineOpen(false);
    setRefineError(null);
    setReelPreviewError(null);
    setRebuildingReel(false);
    if (reelRebuilt && workingJob.status === 'delivered') {
      // Sync parent with the rebuilt job (poll will resolve immediately).
      onRefineStarted?.(workingJob);
    }
  };

  const handleAccept = async (
    music?: RefineMusicOptions | null
  ): Promise<boolean> => {
    if (!workingJob.id || acceptBusy) return false;
    setAcceptBusy(true);
    setAcceptError(null);
    setPaywall(null);
    try {
      const { job: nextJob } = await submitAccept(
        workingJob.id,
        musicEnabled ? music ?? null : undefined
      );
      await onEntitlementRefresh?.();
      onAcceptStarted?.(nextJob);
      return true;
    } catch (err) {
      if (isUpgradeRequiredError(err)) {
        setPaywall('upgrade');
        setAcceptError(
          isStudioBillingEnabled()
            ? STUDIO_COPY.paywallHeadline
            : STUDIO_COPY.betaLimitReached
        );
      } else if (isMonthlyCapReachedError(err)) {
        setPaywall('cap');
        setAcceptError(
          isStudioBillingEnabled()
            ? STUDIO_COPY.capReachedTemplate
                .replace(
                  '{period_cap}',
                  String(err.period_cap ?? entitlement?.period_cap ?? '')
                )
                .replace(
                  '{period_resets_at}',
                  err.period_resets_at
                    ? new Date(err.period_resets_at).toLocaleDateString()
                    : 'next cycle'
                )
            : STUDIO_COPY.betaLimitReached
        );
      } else if (err instanceof AcquireError) {
        setAcceptError(err.message);
      } else {
        setAcceptError('Could not approve this reel. Try again.');
      }
      return false;
    } finally {
      setAcceptBusy(false);
    }
  };

  /**
   * Approve from Customize: finalize the curated set via existing /accept.
   * Unsaved edits rebuild first so the clean reel matches the adjusted preview.
   */
  const handleCustomizeApprove = async (
    clips: RefineClip[],
    meta: { dirty: boolean },
    music?: RefineMusicOptions | null
  ) => {
    if (acceptBusy || refineBusy || rebuildingReel) return;
    if (meta.dirty) {
      const rebuilt = await handleRefineSubmit(clips, music);
      if (!rebuilt) return;
    }
    const ok = await handleAccept(music);
    if (ok) {
      setRefineOpen(false);
      setRefineError(null);
      setReelPreviewError(null);
    }
  };

  const currentTier: ReelDurationTier =
    workingJob.reel_duration_tier === 'short' ||
    workingJob.reel_duration_tier === 'long'
      ? workingJob.reel_duration_tier
      : 'mid';
  const showDurationSwitch = Boolean(
    entitlement?.duration_tiers_enabled &&
      entitlement?.render_from_cache_enabled &&
      workingJob.status === 'delivered' &&
      workingJob.id
  );

  const handleDurationSwitch = async (tier: ReelDurationTier) => {
    if (!workingJob.id || durationBusy || recutBusy || tier === currentTier) return;
    setDurationBusy(true);
    setDurationFailure(null);
    try {
      const result = await submitDurationRerender(workingJob.id, tier);
      onRefineStarted?.(result.job);
    } catch (err) {
      setDurationFailure(mapDurationRerenderError(err));
    } finally {
      setDurationBusy(false);
    }
  };

  const handleDurationRescan = () => {
    if (!onRecut || recutBusy || durationBusy) return;
    const name = normalizeReelName(recutName);
    if (!isReelNameValid(name)) return;
    void onRecut(name, { force: true });
  };

  if (refineOpen && refinePayload) {
    const curation = refinePayload.curation;
    return (
      <RefineReviewPanel
        candidates={refinePayload.candidates}
        sourcePreviewUrl={refinePayload.source_preview_url}
        sourcePreviewExpiresAt={refinePayload.source_preview_expires_at}
        onRefreshPreview={() => {
          void refreshPreview();
        }}
        refreshingPreview={refreshingPreview}
        reelPreviewUrl={reelPreviewUrl}
        rebuildingReel={rebuildingReel}
        reelPreviewError={reelPreviewError}
        curateEnabled={
          curateEnabled || Boolean(refinePayload.curate_enabled)
        }
        musicEnabled={musicEnabled}
        initialMusic={initialMusic}
        maxClips={curation?.curate_max_clips ?? 20}
        maxDurationSec={curation?.curate_max_duration_sec ?? 180}
        submitting={refineBusy}
        error={refineError}
        onSubmit={(clips, music) => {
          void handleRefineSubmit(clips, music);
        }}
        onCancel={closeRefine}
        acceptEnabled={preview}
        acceptBusy={acceptBusy}
        acceptError={acceptError}
        acceptCostHint={acceptCostCopy(entitlement)}
        onApprove={(clips, meta, music) => {
          void handleCustomizeApprove(clips, meta, music);
        }}
        job={workingJob}
        entitlement={entitlement}
        videoRef={reelVideoRef}
        recordDock={recordDock}
        onRecordingChange={handleRecordingChange}
        onJobUpdated={(next) => {
          setWorkingJob(next);
          setReelPreviewUrl(next.signed_url ?? null);
        }}
        reelName={
          isReelNameValid(workingJob.reel_name)
            ? normalizeReelName(workingJob.reel_name)
            : recutName
        }
        onReelNameChange={setRecutName}
      />
    );
  }

  const showAddOnsWorkspace =
    musicEnabled && workingJob.status === 'delivered';
  const playerBlock = (
    <div
      ref={playerRef}
      className={`studio-result-player${voiceRecording ? ' is-recording' : ''}`}
      data-testid="studio-result-player"
      data-recording={voiceRecording ? 'true' : 'false'}
    >
      <div className="studio-result-player-frame">
        {signedUrl ? (
          <video
            key={previewVersion || signedUrl}
            ref={reelVideoRef}
            className="w-full aspect-video bg-black"
            controls
            playsInline
            poster={workingJob.poster_url ?? undefined}
            src={playerSrc}
            data-testid="studio-result-video"
          />
        ) : (
          <div
            className="aspect-video flex items-center justify-center text-[var(--studio-muted)]"
            data-testid="studio-result-video-missing"
          >
            Reel URL unavailable
          </div>
        )}
      </div>
      <div
        ref={setRecordDock}
        className="studio-result-record-dock"
        data-testid="studio-result-record-dock"
      />
    </div>
  );

  return (
    <div className="space-y-8" data-testid="studio-result">
      {alreadyExists ? (
        <p
          className="text-center text-sm text-[var(--studio-muted)]"
          data-testid="studio-already-exists"
        >
          {STUDIO_COPY.alreadyExists}
        </p>
      ) : null}

      <div className="space-y-3 text-center">
        <div className="studio-tape-stripe rounded-sm opacity-60" aria-hidden />
        {preview ? (
          <p
            className="text-xs uppercase tracking-wide text-[var(--studio-muted)]"
            data-testid="studio-preview-badge"
          >
            {STUDIO_COPY.previewBadge}
          </p>
        ) : null}
        <h1 className="text-3xl font-semibold" data-testid="studio-result-headline">
          {preview ? STUDIO_COPY.previewHeadline : STUDIO_COPY.resultHeadline}
        </h1>
        {preview ? (
          <p
            className="studio-prose text-sm text-[var(--studio-muted)]"
            data-testid="studio-preview-blurb"
          >
            {STUDIO_COPY.previewBlurb}
          </p>
        ) : null}
        {durationSec != null ? (
          <p
            className="text-sm text-[var(--studio-muted)]"
            data-testid="studio-result-duration"
          >
            {formatDuration(durationSec)} game recap
          </p>
        ) : null}
      </div>

      {showAddOnsWorkspace ? (
        <div className="studio-addons-workspace">
          <div className="studio-addons-workspace-preview">{playerBlock}</div>
          <div className="studio-addons-workspace-controls">
            <AddOnsPanel
              job={workingJob}
              entitlement={entitlement}
              videoRef={reelVideoRef}
              recordDock={recordDock}
              onRecordingChange={handleRecordingChange}
              onJobUpdated={(next) => {
                setWorkingJob(next);
                setReelPreviewUrl(next.signed_url ?? null);
              }}
            />
          </div>
        </div>
      ) : (
        playerBlock
      )}

      {preview ? (
        <div className="space-y-3 text-center" data-testid="studio-accept">
          <p className="text-sm text-[var(--studio-muted)]">
            {acceptCostCopy(entitlement)}
          </p>
          <p className="text-xs text-[var(--studio-muted)]">
            {STUDIO_COPY.acceptCleanHint}
          </p>
          <button
            type="button"
            className="studio-btn-primary"
            data-testid="studio-accept-cta"
            disabled={acceptBusy || !workingJob.id}
            onClick={() => {
              void handleAccept();
            }}
          >
            {acceptBusy
              ? STUDIO_COPY.acceptCtaBusy
              : STUDIO_COPY.acceptCtaWithCost}
          </button>
          {acceptError ? (
            <p
              className="text-sm text-[var(--studio-danger)]"
              role="alert"
              data-testid="studio-accept-error"
            >
              {acceptError}
            </p>
          ) : null}
          {paywall === 'cap' ? (
            <p className="text-sm text-[var(--studio-muted)]">
              {STUDIO_COPY.capReachedRenewal}
            </p>
          ) : null}
          {paywall === 'upgrade' ? (
            <p className="text-sm text-[var(--studio-muted)]" data-testid="studio-accept-paywall">
              {STUDIO_COPY.paywallHeadline}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-center gap-3">
          {signedUrl ? (
            <ReelDownloadButton
              url={signedUrl}
              className="studio-btn-primary no-underline"
              testId="studio-result-download"
            />
          ) : null}
          <button
            type="button"
            className="studio-btn-ghost"
            onClick={() => void handleCopy()}
            data-testid="studio-result-copy"
          >
            {copied ? STUDIO_COPY.copiedConfirm : 'Copy link'}
          </button>
        </div>
      )}
      {!preview ? (
        <p className="text-center text-xs text-[var(--studio-muted)]">
          {STUDIO_COPY.copyLinkNote}
        </p>
      ) : null}

      {showCurationEntry ? (
        <div
          className="rounded-lg border border-white/10 bg-white/[0.03] p-5 space-y-3 text-center"
          data-testid={curateEnabled ? 'studio-customize-cta' : 'studio-refine-cta'}
        >
          <p className="text-sm text-[var(--studio-muted)]">
            {curateEnabled
              ? STUDIO_COPY.customizeBlurb
              : STUDIO_COPY.refineNoDockNote}
          </p>
          <button
            type="button"
            className="studio-btn-ghost"
            data-testid={curateEnabled ? 'studio-customize-open' : 'studio-refine-open'}
            disabled={refineLoading}
            onClick={() => {
              void openRefine();
            }}
          >
            {refineLoading
              ? 'Loading clips…'
              : curateEnabled
                ? STUDIO_COPY.customizeOpen
                : STUDIO_COPY.refineOpen}
          </button>
          {refineError ? (
            <p className="text-sm text-[var(--studio-danger)]" role="alert">
              {refineError}
            </p>
          ) : null}
        </div>
      ) : null}

      {showDurationSwitch ? (
        <div
          className="rounded-lg border border-white/10 bg-white/[0.03] p-5 space-y-3"
          data-testid="studio-duration-switch"
        >
          <DurationTierPicker
            value={currentTier}
            onChange={(tier) => {
              void handleDurationSwitch(tier);
            }}
            disabled={durationBusy || recutBusy}
            compact
            idPrefix="studio-duration-switch"
          />
          <p className="text-sm text-[var(--studio-muted)]">
            {durationBusy
              ? STUDIO_COPY.durationSwitchBusy
              : STUDIO_COPY.durationSwitchHint}
          </p>
          {durationFailure ? (
            <div
              className="space-y-2"
              data-testid="studio-duration-error"
              data-error-code={durationFailure.code}
            >
              <p className="text-sm text-[var(--studio-danger)]" role="alert">
                {durationFailure.message}
              </p>
              {durationFailure.offerRescan && onRecut && entitlement?.recut_enabled ? (
                <>
                  {!isReelNameValid(recutName) ? (
                    <ReelNameField
                      id="studio-duration-rescan-reel-name"
                      value={recutName}
                      onChange={setRecutName}
                      showError
                      disabled={recutBusy}
                    />
                  ) : null}
                  <button
                    type="button"
                    className="studio-btn-ghost"
                    data-testid="studio-duration-rescan"
                    disabled={recutBusy || durationBusy || !isReelNameValid(recutName)}
                    onClick={handleDurationRescan}
                  >
                    {recutBusy ? STUDIO_COPY.recutBusy : STUDIO_COPY.durationSwitchRescan}
                  </button>
                  <p className="text-xs text-[var(--studio-muted)]">
                    {STUDIO_COPY.durationSwitchRescanHint}
                  </p>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {showRecut ? (
        <div
          className="rounded-lg border border-white/10 bg-white/[0.03] p-5 space-y-3 text-center"
          data-testid="studio-recut"
        >
          {!confirmOpen ? (
            <>
              <p className="text-sm text-[var(--studio-muted)]">{recutCostCopy(entitlement)}</p>
              <button
                type="button"
                className="studio-btn-ghost"
                data-testid="studio-recut-open"
                disabled={recutBusy}
                onClick={() => setConfirmOpen(true)}
              >
                {STUDIO_COPY.recutButton}
              </button>
            </>
          ) : (
            <>
              <p className="font-medium">{STUDIO_COPY.recutConfirmTitle}</p>
              <p className="text-sm text-[var(--studio-muted)]">{recutCostCopy(entitlement)}</p>
              <ReelNameField
                id="studio-recut-reel-name"
                value={recutName}
                onChange={setRecutName}
                showError={!isReelNameValid(recutName)}
                disabled={recutBusy}
              />
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  className="studio-btn-primary"
                  data-testid="studio-recut-confirm"
                  disabled={recutBusy || !isReelNameValid(recutName)}
                  onClick={() => {
                    const name = normalizeReelName(recutName);
                    if (!isReelNameValid(name)) return;
                    void onRecut?.(name, { force: false });
                  }}
                >
                  {recutBusy
                    ? STUDIO_COPY.recutBusy
                    : entitlement?.recut_reuse_source
                      ? STUDIO_COPY.recutConfirmReuse
                      : entitlement?.signoff_billing_enabled
                        ? STUDIO_COPY.recutConfirmSignoff
                        : STUDIO_COPY.recutConfirm}
                </button>
                {entitlement?.recut_reuse_source ? (
                  <button
                    type="button"
                    className="studio-btn-ghost studio-recut-rescan"
                    data-testid="studio-recut-rescan"
                    disabled={recutBusy || !isReelNameValid(recutName)}
                    onClick={() => {
                      const name = normalizeReelName(recutName);
                      if (!isReelNameValid(name)) return;
                      void onRecut?.(name, { force: true });
                    }}
                  >
                    {STUDIO_COPY.recutRescan}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="studio-btn-ghost"
                  data-testid="studio-recut-cancel"
                  disabled={recutBusy}
                  onClick={() => setConfirmOpen(false)}
                >
                  {STUDIO_COPY.recutCancel}
                </button>
              </div>
            </>
          )}
          {recutError ? (
            <p className="text-sm text-[var(--studio-danger)]" role="alert">
              {/generation_ceiling/i.test(recutError)
                ? STUDIO_COPY.generationCeilingHint
                : recutError}
            </p>
          ) : null}
        </div>
      ) : null}

      {isStudioBillingEnabled() && (showFreeUpsell || paywall === 'upgrade') ? (
        <div
          className="studio-upsell studio-upsell-prominent rounded-lg p-6 md:p-10 space-y-5 text-center"
          data-testid="studio-result-upsell"
        >
          <p className="text-xl md:text-2xl font-semibold text-[var(--studio-text)] leading-snug">
            {STUDIO_COPY.paywallHeadline}
          </p>
          <p className="studio-prose text-[var(--studio-muted)] leading-relaxed">
            {STUDIO_COPY.watermarkUpsell}
          </p>
          <Link
            to="/studio/upgrade"
            className="studio-btn-primary no-underline inline-flex text-base px-8 py-3"
            data-testid="studio-result-upgrade-cta"
          >
            {STUDIO_COPY.upgradeCta}
          </Link>
          {isStudioReelPacksEnabled() ? (
            <Link
              to="/studio/upgrade"
              className="studio-btn-ghost no-underline inline-flex text-base px-8 py-3"
              data-testid="studio-result-pack-cta"
            >
              {STUDIO_COPY.pricingPackCta}
            </Link>
          ) : null}
          <p className="text-xs text-[var(--studio-muted)]">{STUDIO_COPY.cancelAnytime}</p>
        </div>
      ) : null}

      {!isStudioBillingEnabled() && paywall === 'upgrade' ? (
        <div
          className="rounded-lg border border-[var(--studio-border)] bg-[var(--studio-bg-elevated)] p-6 text-center space-y-2"
          data-testid="studio-result-beta-limit"
          role="status"
        >
          <p className="text-xl font-semibold">{STUDIO_COPY.betaLimitReached}</p>
          <p className="text-sm text-[var(--studio-muted)]">
            {STUDIO_COPY.betaLimitReachedBody}
          </p>
        </div>
      ) : null}

      {isPaid && !preview ? (
        <p
          className="text-center text-sm text-[var(--studio-muted)]"
          data-testid="studio-result-allotment"
        >
          {formatAllotmentCopy(
            entitlement.period_reels_remaining ??
              remainingAccepted(entitlement),
            entitlement.period_cap ?? entitlement.accepted_max ?? STUDIO_PRICING.paidCap
          )}
        </p>
      ) : null}

      {(entitlement?.reel_credits ?? 0) > 0 ? (
        <p
          className="text-center text-sm text-[var(--studio-text)]"
          data-testid="studio-result-credit-balance"
        >
          {formatCreditBalanceCopy(entitlement?.reel_credits ?? 0)}
        </p>
      ) : null}

      <div className="text-center">
        <Link to="/studio/vods" className="studio-btn-ghost no-underline inline-flex">
          Back to VODs
        </Link>
      </div>
    </div>
  );
};

export default ReelResultPanel;
